import React, {useState} from 'react';
import { statuses, useScheduleSuggestion } from '../../../../Data/Jobs';
import { classNames, dateToString, weeksInYear, weekString } from '../../../../Services/helpers';
import Button from '../../../../Components/Button/Button';
import Dropdown from '../../../../Components/Dropdown';
import Icon from '../../../../Components/Icons/Icon';
import Loading, { PageLoading } from '../../../../Components/Loading';
import Money from '../../../../Components/Formatting/Money';
import RelativeWeek from '../../../../Components/Formatting/RelativeWeek';
import Schedule from './Schedule';
import DateSquare from './DateSquare';
import Compact from './Compact';
import JobDot from './JobDot';

const SuggestedDates = () => {
    const thisWeek = weekString();
    const [week, setWeek] = useState(weekString());
    // Don't include the week string for the current week, so we can preload it
    const {dates, isLoading, refresh} = useScheduleSuggestion(week === thisWeek ? null : week, true);
    const [scheduling, setScheduling] = useState(null);
    const [expanded, setExpanded] = useState(false);

    const prevWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w--;
        if (w < 1) {
            y--;
            w = 52;
        }
        setWeek(`${y}-W${w}`);
    }
    const nextWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w++;
        if (w > weeksInYear(y)) {
            y++;
            w = 1;
        }
        setWeek(`${y}-W${w}`);
    }

    const today = dateToString(new Date());

    return <div>
        <div className="flex flex-row justify-between items-center mb-2">
            <h3 className="text-lg font-bold text-brand grow shrink-0">
                Week overview
                {week !== thisWeek && <span className="ml-2 font-normal text-muted hidden md:inline-block">
                    (<RelativeWeek week={week} />)
                </span>}
                {isLoading && <Loading className="ml-2" />}
            </h3>
            <div className="flex flex-row gap-x-5">
                <Icon icon="LeftChevron" onClick={prevWeek} />
                <Icon icon="RightChevron" onClick={nextWeek} />
                <Icon icon={expanded ? 'ArrowsDiagonalIn' : 'ArrowsDiagonalOut'}
                    onClick={() => setExpanded(!expanded)}
                />
            </div>
        </div>

        {dates && <>
            <Inner {...{expanded, setExpanded, today, isLoading, setScheduling, dates}} />
            <Schedule isOpen={!!scheduling}
                setIsOpen={setScheduling}
                date={scheduling}
                rounds={dates[scheduling]?.suggested?.rounds ?? null}
                jobs={dates[scheduling]?.suggested?.jobs ?? null}
                onScheduled={() => refresh()}
            />
        </>}
        {!dates && <PageLoading />}
    </div>
}

const Inner = ({expanded, setExpanded, today, isLoading, setScheduling, dates}) => {
    if (!expanded) {
        return <Compact {...{today, dates, setExpanded}} />
    }

    return <div className="flex flex-col">
        {Object.keys(dates).map(date => {
            return <Row key={date} {...{today, date, isLoading, setScheduling, suggestion: dates[date]}} />
        })}
    </div>
}

const Row = ({today, date, suggestion, isLoading, setScheduling}) => {

    const noScheduledJobs = suggestion.scheduled.jobs.length === 0 &&
                            suggestion.scheduled.rounds.every(round => round.jobs.length === 0);

    const noSuggestedJobs = suggestion.suggested.jobs.length === 0 &&
                            suggestion.suggested.rounds.every(round => round.jobs.length === 0);

    return <div className={classNames(
            'w-full pb-3 mb-3 border-b last:border-none flex flex-row gap-x-3 justify-between cursor',
            (noScheduledJobs && noSuggestedJobs) ? 'items-center' : 'items-top'
        )}>
        <div className="shrink-0 self-start">
            <DateSquare {...{date, today}} />
        </div>
        <div className="grow text-sm">

            <RoundsAndJobs rounds={suggestion.scheduled.rounds} jobs={suggestion.scheduled.jobs} />

            {noScheduledJobs && <p className="text-muted">
                No jobs scheduled
            </p>}

            {!noSuggestedJobs && <div
                    className="mt-2 p-2 bg-slate-100 border border-slate-200 rounded-lg flex flex-row justify-between items-center"
                >
                <div>
                    <p className="mb-1 font-bold">
                        Suggested jobs for {new Date(date).toLocaleDateString('en-GB', {weekday: 'long'})}
                    </p>

                    <RoundsAndJobs rounds={suggestion.suggested.rounds} jobs={suggestion.suggested.jobs} />
                </div>
                <div>
                    <Button theme="primary"
                        label="Schedule"
                        disabled={isLoading}
                        onClick={() => setScheduling(date)}
                        zeroMargin
                    />
                </div>
            </div>}

        </div>
        <div className="shrink-0">
            <Dropdown
                disabled={isLoading}
                options={[
                    {
                        icon: 'RightChevron',
                        title: 'View suggested jobs',
                        to: `/plan/jobs?suggestedDate=${date}`
                    }
                ]}
            />
        </div>
    </div>
}

const RoundsAndJobs = ({rounds, jobs}) => {
    return <>
        {rounds.map(round => <JobsRow key={round.id} title={round.name} jobs={round.jobs} />)}

        {jobs.length > 0 && <JobsRow
            title="Jobs without rounds"
            jobs={jobs}
        />}
    </>
}

const JobsRow = ({title, jobs}) => {

    const totalValue = jobs.reduce(
        (agg, job) => statuses?.[job.status]?.excludeFromTotals ? agg : agg + job.cost,
        0
    );

    return <div>
        <p>{title} <span className="text-muted"> &nbsp; - &nbsp; <Money pence={totalValue} /></span></p>
        <div className="flex flex-row flex-wrap gap-1 my-1 md:max-w-[90%]">
            {jobs.map(job => <JobDot key={job.id} job={job} />)}
        </div>
    </div>
}


export default SuggestedDates;